import { useState } from 'react';
import { Anchor, Button, Card, Code, Group, Modal, SimpleGrid, Stack, Table, Tabs, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { isTerminal, useCancelRun, useRefreshRun, useRunPayloads, type RunDetail as RunDetailData } from '@/api/runs';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { UriCell } from '@/features/staff/UriCell';
import { useFormat } from '@/components/useFormat';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { InternalStateChip } from '@/features/staff/InternalStateChip';
import { brand } from '@/theme/theme';
import { FailureBadge, SchedulingNote } from './FailureBadge';
import { FindingsList, type Finding } from './FindingsList';
import { RunLogs } from './RunLogs';
import { runFailure } from './runError';
import { StagesProgress } from './StagesProgress';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <Text fz={14} component="div">
        {children}
      </Text>
    </Stack>
  );
}

function Payloads({ runId }: { runId: string }) {
  const { t } = useTranslation('runs');
  const { dateTime } = useFormat();
  const q = useRunPayloads(runId);
  if (!q.data) return null;
  return (
    <Stack gap="md">
      <Title order={4}>{t('payloads.request')}</Title>
      <Code block fz={11}>
        {JSON.stringify(q.data.request, null, 2)}
      </Code>
      <Title order={4}>{t('payloads.deliveries', { count: q.data.deliveries.length })}</Title>
      {q.data.deliveries.map((d) => (
        <Card key={d.event_id} p="sm">
          <Group gap="sm" mb={4}>
            <Text ff="monospace" fz={12}>
              {d.event_id}
            </Text>
            <Text fz={12} c={brand.textMuted}>
              seq {d.sequence} · {dateTime(d.received_at)} · {d.applied ? t('payloads.applied') : t('payloads.ignored')}
              {d.note ? ` (${d.note})` : ''}
            </Text>
          </Group>
          <Code block fz={11}>
            {JSON.stringify(d.payload, null, 2)}
          </Code>
        </Card>
      ))}
    </Stack>
  );
}

export const RUN_TABS = ['overview', 'logs', 'payloads'] as const;
export type RunTab = (typeof RUN_TABS)[number];

/** `tab` / `onTabChange` let the route keep the open tab in the URL, so "Logs" can be linked. */
export function RunDetail({ run, tab, onTabChange }: { run: RunDetailData; tab?: RunTab; onTabChange?: (tab: RunTab) => void }) {
  const { t } = useTranslation('runs');
  const { t: tc } = useTranslation();
  const { dateTime, bytes } = useFormat();
  const kindLabel = useKindLabel();
  const cancel = useCancelRun();
  const refresh = useRefreshRun();
  const [confirm, setConfirm] = useState(false);
  const terminal = isTerminal(run.state);
  const result = (run.result ?? {}) as Record<string, unknown>;
  const findings = Array.isArray(result.findings) ? (result.findings as Finding[]) : [];
  const error = run.error as Record<string, unknown> | null | undefined;
  const failure = runFailure(error);
  const resources = (run.resources_override ?? {}) as Record<string, unknown>;

  const doCancel = async () => {
    try {
      await cancel.mutateAsync(run.public_id);
      setConfirm(false);
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  const doRefresh = async () => {
    try {
      const r = await refresh.mutateAsync(run.public_id);
      notifications.show({ color: 'forest', message: r.advanced ? t('actions.refreshed') : t('actions.refreshUnchanged') });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Stack gap="lg">
      <StaffBreadcrumbs projectId={run.project} current={run.public_id} />
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="sm">
            <Title order={1} ff="monospace" fz={22}>
              {run.public_id}
            </Title>
            <InternalStateChip state={run.state ?? ''} />
            <SchedulingNote priority={run.priority} allowOverflow={run.allow_overflow} />
            {run.stalled_at && (
              <Text fz={12} c="red" fw={600}>
                {t('states.stalled')}
              </Text>
            )}
          </Group>
          <Text fz={13} c={brand.textMuted}>
            {run.type} {run.version}
            {run.data_object && (
              <>
                {' · '}
                <Link to="/staff/objects/$objectId" params={{ objectId: run.data_object }} style={{ color: brand.textMuted }}>
                  {run.data_object}
                </Link>
              </>
            )}
          </Text>
        </Stack>
        <Group gap="sm">
          <Tooltip label={t('actions.refreshUnsubmitted')} disabled={!!run.external_id}>
            <Button variant="default" disabled={!run.external_id} loading={refresh.isPending} onClick={() => void doRefresh()}>
              {t('actions.refresh')}
            </Button>
          </Tooltip>
          <Button color="red" variant="light" disabled={terminal} onClick={() => setConfirm(true)}>
            {t('actions.cancel')}
          </Button>
        </Group>
      </Group>

      <Tabs
        defaultValue="overview"
        value={tab}
        onChange={onTabChange ? (v) => v && onTabChange(v as RunTab) : undefined}
      >
        <Tabs.List>
          <Tabs.Tab value="overview">{t('tabs.overview')}</Tabs.Tab>
          <Tabs.Tab value="logs">{t('tabs.logs')}</Tabs.Tab>
          <Tabs.Tab value="payloads">{t('tabs.payloads')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="lg">
            <Card>
              <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
                <Field label="external_id">
                  {run.forun_url ? (
                    <Anchor href={run.forun_url} target="_blank" rel="noopener" fz={14}>
                      {run.external_id}
                    </Anchor>
                  ) : (
                    run.external_id || '—'
                  )}
                </Field>
                <Field label="external_ref">{run.external_ref || '—'}</Field>
                <Field label="sequence">{run.sequence}</Field>
                <Field label="attempts">{run.attempts}</Field>
                <Field label="created_at">{dateTime(run.created_at)}</Field>
                <Field label="started_at">{dateTime(run.started_at)}</Field>
                <Field label="finished_at">{dateTime(run.finished_at)}</Field>
                <Field label="last_event_at">{dateTime(run.last_event_at)}</Field>
                <Field label="triggered_by">{run.triggered_by ? run.triggered_by.full_name || run.triggered_by.public_id : t('system')}</Field>
                <Field label="superseded_by">
                  {run.superseded_by ? (
                    <Link to="/staff/runs/$runId" params={{ runId: run.superseded_by }}>
                      {run.superseded_by}
                    </Link>
                  ) : (
                    '—'
                  )}
                </Field>
                <Field label="output_uri">
                  <UriCell uri={run.output_uri} full />
                </Field>
                <Field label="current_stage">{run.current_stage || '—'}</Field>
              </SimpleGrid>
            </Card>
            <Card>
              <Title order={4} mb="sm">
                {t('sections.progress')}
              </Title>
              <StagesProgress stages={run.stages} progress={run.progress} />
            </Card>
            {error && (
              <Card style={{ borderColor: 'rgba(220,38,38,0.4)' }}>
                <Group gap="sm" mb="xs">
                  <Title order={4} c="red">
                    {t('sections.error')} · {failure?.code ?? ''}
                  </Title>
                  <FailureBadge category={failure?.category} />
                </Group>
                {failure?.step && (
                  <Text fz={12} c={brand.textMuted} mb={4}>
                    {t('failure.step')}: {failure.step}
                  </Text>
                )}
                {failure?.message && (
                  <Text fz={13} mb="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {failure.message}
                  </Text>
                )}
                {failure?.hint && (
                  <Text fz={13} mb="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <Text span fw={600} fz={13}>
                      {t('failure.hint')}:
                    </Text>{' '}
                    {failure.hint}
                  </Text>
                )}
                <Code block fz={11}>
                  {JSON.stringify(error, null, 2)}
                </Code>
                {run.submit_error && (
                  <Text fz={12} c={brand.textMuted} mt="xs">
                    {run.submit_error}
                  </Text>
                )}
              </Card>
            )}
            <Card>
              <Title order={4} mb="sm">
                {t('sections.findings')}
              </Title>
              <FindingsList findings={findings} />
            </Card>
            <Card p={0}>
              <Title order={4} px="md" py="sm">
                {t('sections.storage')}
              </Title>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('storage.object')}</Table.Th>
                    <Table.Th>{t('storage.kind')}</Table.Th>
                    <Table.Th>{t('storage.state')}</Table.Th>
                    <Table.Th>{t('storage.bytes')}</Table.Th>
                    <Table.Th>{t('storage.rawBytes')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {run.objects.map((o) => (
                    <Table.Tr key={o.public_id}>
                      <Table.Td>
                        <Link to="/staff/objects/$objectId" params={{ objectId: o.public_id }} style={{ color: brand.text, fontWeight: 600 }}>
                          {o.name || o.public_id}
                        </Link>
                      </Table.Td>
                      <Table.Td>{kindLabel(o.kind)}</Table.Td>
                      <Table.Td>
                        <InternalStateChip state={o.state} />
                      </Table.Td>
                      <Table.Td>{bytes(o.total_bytes)}</Table.Td>
                      <Table.Td>{bytes(o.raw_bytes)}</Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Text fw={700}>{t('storage.total')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>{bytes(run.objects.reduce((n, o) => n + o.total_bytes, 0))}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>{bytes(run.objects.reduce((n, o) => n + o.raw_bytes, 0))}</Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Card>
            <Card p={0}>
              <Title order={4} px="md" py="sm">
                {t('sections.inputs')}
              </Title>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>role</Table.Th>
                    <Table.Th>object</Table.Th>
                    <Table.Th>kind</Table.Th>
                    <Table.Th>uri</Table.Th>
                    <Table.Th>digest</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(run.inputs as Array<Record<string, string>>).map((i, n) => (
                    <Table.Tr key={n}>
                      <Table.Td>{i.role}</Table.Td>
                      <Table.Td>
                        <Link to="/staff/objects/$objectId" params={{ objectId: i.object }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {i.object}
                        </Link>
                      </Table.Td>
                      <Table.Td>{i.kind}</Table.Td>
                      <Table.Td>
                        <Text ff="monospace" fz={11}>
                          {i.uri}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text ff="monospace" fz={11}>
                          {i.digest ?? '—'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
            <Card>
              <Title order={4} mb="sm">
                {t('sections.config')}
              </Title>
              <Code block fz={11}>
                {JSON.stringify(run.config, null, 2)}
              </Code>
              {Object.keys(resources).length > 0 && (
                <>
                  <Text fz={12} fw={700} mt="sm" mb={4}>
                    {t('scheduling.resources')}
                  </Text>
                  <Code block fz={11}>
                    {JSON.stringify(resources, null, 2)}
                  </Code>
                </>
              )}
            </Card>
            <Card>
              <Title order={4} mb="sm">
                {t('sections.result')}
              </Title>
              <Code block fz={11}>
                {JSON.stringify({ outputs: run.outputs_reported, result: run.result }, null, 2)}
              </Code>
            </Card>
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="logs" pt="md">
          <RunLogs runId={run.public_id} active={!terminal} />
        </Tabs.Panel>
        <Tabs.Panel value="payloads" pt="md">
          <Payloads runId={run.public_id} />
        </Tabs.Panel>
      </Tabs>

      <Modal opened={confirm} onClose={() => setConfirm(false)} title={t('actions.cancel')}>
        <Stack gap="md">
          <Text fz={14}>{t('actions.confirmCancel')}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirm(false)}>
              {tc('common.cancel')}
            </Button>
            <Button color="red" loading={cancel.isPending} onClick={() => void doCancel()}>
              {t('actions.cancel')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
